/* eslint-disable @typescript-eslint/naming-convention */

import React, { createContext, useContext } from 'react';
import type {
  CategoryPageSubheaderEntry,
  CustomTagFilterConfig,
  Events,
  ListPageEntry,
  ModuleRegistryEntry,
  RemoteModuleRegistry,
  StoreFrontCarouselTitleProps,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import type { CategoryPageBelowListIterable } from 'src/dispensary/category/category-page-below-list';

type FallbackProps = {
  fallback?: React.FC<any>;
  pending?: React.FC<any>;
  renderRemote?: (component: React.ReactElement) => React.ReactElement;
};
type DefaultComponentSig = (options?: FallbackProps) => React.FC<any>;
type CategoryIterable = { category: ListPageEntry['category']; components: React.ElementType[] };

export type RouteablePage = {
  path: string;
  component: DefaultComponentSig;
};

// The `theme` prop forwarded verbatim to remote (module-federation) components — see
// remote-theme.tsx's `wrapComponentFactory` and README.md's "Accept and use the data and theme
// props". Today it's always the styled-components/MUI theme object built by `getStyledTheme`
// (theme/builders/styled.js, untyped JS) — never a string — but nothing in this subsystem reads
// any of its properties, so it's typed as an opaque plain object rather than that builder's
// specific (currently unnamed) shape.
export type RemoteTheme = Record<string, unknown> | null;

// TEMPORARY: Local shims for smart tags until @dutchiesdk/ecommerce-extensions-sdk exports them (ECOMM-12637)
export type CustomSmartTagFilterConfig = {
  category: string;
  name: string;
  title: string;
  match: string[];
};

/**
 * A safe subset of Puck's own `Field` union: `text`, `textarea`, `number`, `select`, and
 * `radio` only. Deliberately excludes `array`, `object`, `external`, `custom`, `richtext`, and
 * `slot` -- those either carry function values (`CustomField.render`, `ExternalField.fetchList`)
 * that cannot cross a module-federation boundary as plain data, or recurse into themselves. A
 * remote theme declares params as inert metadata; the host turns them into real Puck fields (see
 * `apps/marketplace/src/cms/components/sdk-custom-components.tsx`), so accepting a
 * function-bearing field type here would mean passing unvetted remote code straight into the
 * editor's own field rendering.
 */
export type CustomComponentParamField =
  | { type: 'number'; label?: string; placeholder?: string; min?: number; max?: number; step?: number }
  | { type: 'radio'; label?: string; options: Array<{ label: string; value: boolean | number | string }> }
  | { type: 'select'; label?: string; options: Array<{ label: string; value: boolean | number | string }> }
  | { type: 'text'; label?: string; placeholder?: string }
  | { type: 'textarea'; label?: string; placeholder?: string };

/**
 * Metadata for one arbitrarily-named, generically-placeable SDK component. Unlike every other
 * `RemoteModuleRegistry` slot, a custom component isn't tied to a fixed storefront location -- an
 * author places each one, individually, by its registry key, on any standalone-page or
 * menu-landing page via the CMS editor's component palette (see `useCmsConfigWithSdkComponents`).
 * Every field besides `component` is optional, matching how little an author needs to just drop a
 * plain component with no configurable params onto a page.
 */
export type CustomComponentDefinition = {
  /** The remote component rendered for this entry. A definition with none is skipped entirely. */
  component?: ModuleRegistryEntry;
  /** Shown as the block's label in the component palette and field panel. Falls back to the registry key. */
  displayName?: string;
  /**
   * NOT shown anywhere in the editor UI today -- Puck core has no visible palette description,
   * and the Puck AI plugin that would have surfaced this as `ai.instructions` chat context was
   * removed (see `apps/marketplace/src/cms/AGENTS.md`'s Puck Gotchas #9/#10). Wired through to
   * the generated block's `ai.instructions` anyway (see `sdk-custom-components.tsx`) as the
   * authoring contract for DexCMS AI (Dex-powered, ECOMM-14552/14560), which needs the same
   * schema for the same reason the old plugin did once it lands. A theme author filling this in
   * gets no author-visible effect yet.
   */
  description?: string;
  /**
   * Puck field definitions for this component's author-editable params, keyed by prop name, and
   * passed straight into the generated Puck block's `fields` -- see `CustomComponentParamField`
   * for which Puck field types are supported and why.
   */
  params?: Record<string, CustomComponentParamField>;
};

/**
 * Keyed by a stable id the remote theme author assigns. This id becomes the Puck block type name
 * once an author places the component on a page (see `sdkComponentBlockName`), so it is baked
 * into saved page content and must never be renamed or reused for a different component.
 */
export type CustomComponentsRegistry = Record<string, CustomComponentDefinition>;

// TODO: ECOMM-13280 - Remove ExtendedRemoteModuleRegistry once @dutchiesdk/ecommerce-extensions-sdk exports SpecialsCard
// TODO: ECOMM-12637 - Remove enableCustomSmartTagFilters once @dutchiesdk/ecommerce-extensions-sdk exports it
// TODO: ECOMM-XXXXX - Remove CustomComponents once @dutchiesdk/ecommerce-extensions-sdk exports it
export type ExtendedRemoteModuleRegistry = RemoteModuleRegistry & {
  SpecialsCard?: ModuleRegistryEntry;
  enableCustomSmartTagFilters?: CustomSmartTagFilterConfig[];
  CustomComponents?: CustomComponentsRegistry;
};

export type RemoteModuleRegistryWithSmartTagFilters = ExtendedRemoteModuleRegistry;

export type ThemeConfig = {
  customTagFilters: CustomTagFilterConfig[];
  customSmartTagFilters: CustomSmartTagFilterConfig[];
};

export type CommerceComponents = {
  components: {
    [K in keyof Omit<
      ExtendedRemoteModuleRegistry,
      | 'CategoryPageBelowList'
      | 'CategoryPageInterstitials'
      | 'CategoryPageSlots'
      | 'CategoryPageSubheaders'
      | 'CustomComponents'
      | 'enableCustomSmartTagFilters'
      | 'enableCustomTagFilters'
      | 'events'
      | 'getStoreFrontMetaFields'
      | 'RouteablePages'
      | 'StoreFrontCarouselInterstitials'
      | 'StoreFrontCarouselTitles'
    >]-?: DefaultComponentSig;
  };
  findCarouselTitle: (o: {
    carouselId: string;
    fallback?: React.FC<StoreFrontCarouselTitleProps>;
  }) => React.FC<StoreFrontCarouselTitleProps>;
  findPage: (o: { fallback?: React.FC; path?: string }) => React.FC;
  /**
   * Resolves one SDK custom component by its registry id -- see `CustomComponentsRegistry`.
   * Mirrors `findPage`'s holding/fallback semantics (blank while the remote theme is still
   * loading, `fallback` once resolved with no matching/valid entry).
   */
  findCustomComponent: (o: { id: string; fallback?: React.FC<any> }) => React.FC<any>;
  /**
   * Raw, validated `CustomComponents` registry metadata (id, displayName, description, params)
   * -- NOT a render-ready component map. Used to build the CMS editor's dynamic SDK Components
   * palette entries (`createSdkCustomComponentBlocks`); use `findCustomComponent` to actually
   * render one.
   */
  customComponents: CustomComponentsRegistry;
  iterables: {
    StoreFrontCarouselInterstitials: () => React.ElementType[];
    CategoryPageInterstitials: () => CategoryIterable[];
    CategoryPageSlots: () => CategoryIterable[];
    CategoryPageBelowList: () => CategoryPageBelowListIterable[];
    /**
     * Not a component -- raw, unresolved SDK-authored entries for the
     * optional category page sub-header (ECOMM-13289). Consumers should
     * resolve/validate the matching entry via
     * `findMatchingCategorySubheaderText` rather than reading `text` off
     * these directly.
     */
    CategoryPageSubheaders: () => CategoryPageSubheaderEntry[];
  };
  pages: ({ fallback }: FallbackProps) => RouteablePage[];
  events: Events | undefined;
  isRemoteThemeResolved: boolean;
  themeConfig: ThemeConfig;
  /**
   * The current dispensary's remote theme module path (`customizationProvider.modulePath` in
   * `useRemoteCommerceComponents`), or `undefined` when no remote theme is configured for it.
   * Changes exactly when the active store's theme identity does -- including a plain store
   * switch to one with no theme at all -- so consumers that need to distinguish "the same
   * theme reloaded" from "a genuinely different theme/store" (e.g.
   * `useCmsConfigWithSdkComponents`'s cross-render accumulator) can key off it directly instead
   * of re-deriving dispensary/theme identity themselves.
   */
  modulePath: string | undefined;
  /**
   * The current store's full remote theme identity -- `manifestUrl`, `scope`, and `modulePath`
   * composed together -- or `undefined` when no remote theme is configured. `modulePath` alone
   * is NOT a safe proxy for "which theme": two different stores/manifests can legitimately
   * expose the same Module Federation `modulePath` (e.g. the conventional `'./Theme'` expose
   * name) while differing only in `manifestUrl`/`scope`, which is exactly what
   * `useLazyRemoteModule` itself keys its own reset on. Consumers that need to distinguish "the
   * same theme reloaded" from "a genuinely different theme/store" (e.g.
   * `useCmsConfigWithSdkComponents`'s cross-render accumulator) should key off this, not
   * `modulePath`.
   */
  themeIdentity: string | undefined;
};

const EmptyComponent: React.FC<any> = () => <></>;

export const defaultCommerceComponents: CommerceComponents = {
  components: {
    StoreFrontHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontNavigation: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontFooter: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    GlobalStyles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoyaltyWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontLoginWidget: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    StoreFrontSpecialsCategoryTiles: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ListPageHero: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsPrimary: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductDetailsMeta: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    ProductCardListItem: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    FilterSidePanel: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsPageHeader: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
    SpecialsCard: ({ fallback = EmptyComponent }: FallbackProps) => fallback,
  },
  findCarouselTitle: ({ fallback = EmptyComponent }) => fallback,
  findPage: ({ fallback = () => <></> }) => fallback,
  findCustomComponent: ({ fallback = EmptyComponent }) => fallback,
  customComponents: {},
  iterables: {
    StoreFrontCarouselInterstitials: () => [],
    CategoryPageInterstitials: () => [],
    CategoryPageSlots: () => [],
    CategoryPageBelowList: () => [],
    CategoryPageSubheaders: () => [],
  },

  pages: (_props: FallbackProps = { fallback: () => <></> }) => [],
  events: {},
  isRemoteThemeResolved: false,
  themeConfig: { customTagFilters: [], customSmartTagFilters: [] },
  modulePath: undefined,
  themeIdentity: undefined,
};

export const CommerceComponentsContext = createContext<CommerceComponents>(defaultCommerceComponents);

export const useCommerceComponents = (): CommerceComponents => useContext(CommerceComponentsContext);
